import { toast } from 'sonner';
import { API_ENDPOINTS, HTTP_METHODS } from '../config/api';
import { BaseApiService, ApiResponse, PaginatedResponse } from './baseApi';
import { Process } from './processesApi';

export interface ProcessStep {
  id: number;
  sequenceOrder: number;
  cycleTime: number;
  setupTime: number;
  runTime: number;
  laborHours: number;
  machineHours: number;
  temperature: number;
  pressure: number;
  speed: number;
  feedRate: number;
  isQualityCheck: boolean;
  qualityParameters: string;
  toleranceMin: number;
  toleranceMax: number;
  isCritical: boolean;
  remarks: string;
  branchId: number;
  createdBy: number;
  updatedBy: number;
  createdAt: string;
  updatedAt: string;
  processFlowId: number;
  process: Process;
}

export interface ProcessFlow {
  id: number;
  name: string; // Human-readable name for the process flow
  processFlowNumber: string;
  version: string;
  status: string;
  description: string;
  totalCycleTime: number;
  setupTime: number;
  runTime: number;
  totalLaborHours: number;
  machineHours: number;
  qualityCheckPoints: number;
  isActive: boolean;
  branchId: number;
  createdBy: number;
  updatedBy: number;
  createdAt: string;
  updatedAt: string;
  processSteps: ProcessStep[];
}

export interface ProcessFlowResponse extends PaginatedResponse<ProcessFlow> {}

export interface ProcessFlowCreateRequest {
  name: string; // Human-readable name for the process flow
  processFlowNumber: string;
  version: string;
  status: string;
  description: string;
  totalCycleTime: number;
  setupTime: number;
  runTime: number;
  totalLaborHours: number;
  machineHours: number;
  qualityCheckPoints: number;
  isActive: boolean;
  branchId: number;
  createdBy: number;
  updatedBy: number;
}

export interface ProcessFlowUpdateRequest extends ProcessFlowCreateRequest {
  id: number;
}

export interface ProcessStepCreateRequest {
  sequenceOrder: number;
  cycleTime: number;
  setupTime: number;
  runTime: number;
  laborHours: number;
  machineHours: number;
  temperature: number;
  pressure: number;
  speed: number;
  feedRate: number;
  isQualityCheck: boolean;
  qualityParameters: string;
  toleranceMin: number;
  toleranceMax: number;
  isCritical: boolean;
  remarks: string;
  branchId: number;
  processFlowId: number;
  processId: number;
  createdBy: number;
  updatedBy: number;
}

export interface ProcessStepUpdateRequest extends ProcessStepCreateRequest {
  id: number;
}

class ProcessFlowApiService extends BaseApiService {
  async getProcessFlows(): Promise<ProcessFlow[]> {
    try {
      const response = await this.makeRequest<ApiResponse<ProcessFlow[]>>(
        API_ENDPOINTS.PROCESS_FLOWS.LIST,
        { method: HTTP_METHODS.GET }
      );
      if (response.success) {
        return response.data;
      }
      throw new Error(response.message || 'Failed to fetch process flows');
    } catch (error) {
      // console.error('Error fetching process flows:', error);
      toast.error('Failed to fetch process flows');
      throw error;
    }
  }

  async getProcessFlowById(id: string): Promise<ProcessFlow> {
    try {
      const response = await this.makeRequest<ApiResponse<ProcessFlow>>(
        API_ENDPOINTS.PROCESS_FLOWS.DETAIL(id),
        { method: HTTP_METHODS.GET }
      );
      
      if (response.success) {
        return response.data;
      } else {
        throw new Error(response.message || 'Failed to fetch process flow');
      }
    } catch (error) {
      // console.error('Error fetching process flow:', error);
      toast.error('Failed to fetch process flow details');
      throw error;
    }
  }

  async createProcessFlow(processFlowData: ProcessFlowCreateRequest): Promise<ProcessFlow> {
    try {
      const response = await this.makeRequest<ApiResponse<ProcessFlow>>(
        API_ENDPOINTS.PROCESS_FLOWS.CREATE,
        {
          method: HTTP_METHODS.POST,
          body: JSON.stringify(processFlowData)
        }
      );
      
      if (response.success) {
        toast.success('Process flow created successfully');
        return response.data;
      } else {
        throw new Error(response.message || 'Failed to create process flow');
      }
    } catch (error) {
      // console.error('Error creating process flow:', error);
      toast.error('Failed to create process flow');
      throw error;
    }
  }

  async updateProcessFlow(id: string, processFlowData: ProcessFlowUpdateRequest): Promise<ProcessFlow> {
    try {
      const response = await this.makeRequest<ApiResponse<ProcessFlow>>(
        API_ENDPOINTS.PROCESS_FLOWS.UPDATE(id),
        {
          method: HTTP_METHODS.PUT,
          body: JSON.stringify(processFlowData)
        }
      );
      
      if (response.success) {
        toast.success('Process flow updated successfully');
        return response.data;
      } else {
        throw new Error(response.message || 'Failed to update process flow');
      }
    } catch (error) {
      // console.error('Error updating process flow:', error);
      toast.error('Failed to update process flow');
      throw error;
    }
  }

  async deleteProcessFlow(id: string): Promise<void> {
    try {
      const response = await this.makeRequest<ApiResponse<void>>(
        API_ENDPOINTS.PROCESS_FLOWS.DELETE(id),
        { method: HTTP_METHODS.DELETE }
      );
      
      if (response.success) {
        toast.success('Process flow deleted successfully');
      } else {
        throw new Error(response.message || 'Failed to delete process flow');
      }
    } catch (error) {
      // console.error('Error deleting process flow:', error);
      toast.error('Failed to delete process flow');
      throw error;
    }
  }

  async searchProcessFlows(searchTerm: string): Promise<ProcessFlow[]> {
    try {
      const response = await this.makeRequest<ApiResponse<ProcessFlow[]>>(
        `${API_ENDPOINTS.PROCESS_FLOWS.SEARCH}?q=${encodeURIComponent(searchTerm)}`,
        { method: HTTP_METHODS.GET }
      );
      
      if (response.success) {
        return response.data;
      } else {
        throw new Error(response.message || 'Failed to search process flows');
      }
    } catch (error) {
      // console.error('Error searching process flows:', error);
      toast.error('Failed to search process flows');
      throw error;
    }
  }

  // Process Step methods
  async getProcessSteps(): Promise<ProcessStep[]> {
    try {
      const response = await this.makeRequest<ApiResponse<ProcessStep[]>>(
        API_ENDPOINTS.PROCESS_STEPS.LIST,
        { method: HTTP_METHODS.GET }
      );
      if (response.success) {
        return response.data;
      }
      throw new Error(response.message || 'Failed to fetch process steps');
    } catch (error) {
      // console.error('Error fetching process steps:', error);
      toast.error('Failed to fetch process steps');
      throw error;
    }
  }

  async getProcessStepsByProcessFlow(processFlowId: number): Promise<ProcessStep[]> {
    try {
      const response = await this.makeRequest<ApiResponse<ProcessStep[]>>(
        API_ENDPOINTS.PROCESS_STEPS.BY_PROCESS_FLOW(processFlowId),
        { method: HTTP_METHODS.GET }
      );
      if (response.success) {
        return response.data;
      }
      throw new Error(response.message || 'Failed to fetch process steps for process flow');
    } catch (error) {
      // console.error('Error fetching process steps for process flow:', error);
      toast.error('Failed to fetch process steps for process flow');
      throw error;
    }
  }

  async createProcessStep(processStepData: ProcessStepCreateRequest): Promise<ProcessStep> {
    try {
      const response = await this.makeRequest<ApiResponse<ProcessStep>>(
        API_ENDPOINTS.PROCESS_STEPS.CREATE,
        {
          method: HTTP_METHODS.POST,
          body: JSON.stringify(processStepData)
        }
      );
      
      if (response.success) {
        toast.success('Process step created successfully');
        return response.data;
      } else {
        throw new Error(response.message || 'Failed to create process step');
      }
    } catch (error) {
      // console.error('Error creating process step:', error);
      toast.error('Failed to create process step');
      throw error;
    }
  }

  async updateProcessStep(id: string, processStepData: ProcessStepUpdateRequest): Promise<ProcessStep> {
    try {
      const response = await this.makeRequest<ApiResponse<ProcessStep>>(
        API_ENDPOINTS.PROCESS_STEPS.UPDATE(id),
        {
          method: HTTP_METHODS.PUT,
          body: JSON.stringify(processStepData)
        }
      );
      
      if (response.success) {
        toast.success('Process step updated successfully');
        return response.data;
      } else {
        throw new Error(response.message || 'Failed to update process step');
      }
    } catch (error) {
      // console.error('Error updating process step:', error);
      toast.error('Failed to update process step');
      throw error;
    }
  }

  async deleteProcessStep(id: string): Promise<void> {
    try {
      const response = await this.makeRequest<ApiResponse<void>>(
        API_ENDPOINTS.PROCESS_STEPS.DELETE(id),
        { method: HTTP_METHODS.DELETE }
      );
      
      if (response.success) {
        toast.success('Process step deleted successfully');
      } else {
        throw new Error(response.message || 'Failed to delete process step');
      }
    } catch (error) {
      // console.error('Error deleting process step:', error);
      toast.error('Failed to delete process step');
      throw error;
    }
  }
}

export const processFlowApiService = new ProcessFlowApiService();
